import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorText, formatDateTime, formatFrist, formatGeld } from '../api';
import { useAuth } from '../auth';
import { KundeFormular } from '../components/KundeFormular';
import { KundenAuswahl } from '../components/KundenAuswahl';
import { TicketNeuDialog } from '../components/Tickets';
import { Badge, Card, Dialog, Empty, Loading, Notice, TYP_SYMBOL, kundenName } from '../components/ui';
import type { Kunde, Telefon, TicketTyp } from '../types';

interface VorgangKurz {
  id: string;
  nummer: string;
  status: string;
  status_bezeichnung: string | null;
  titel: string | null;
  faellig_am: string | null;
}

interface Akte {
  kunde: Kunde & { telefone: Telefon[] };
  bestellungen: VorgangKurz[];
  service: VorgangKurz[];
  lieferungen: VorgangKurz[];
  forderungen: (VorgangKurz & { betrag_cent: number; bezahlt_cent: number; nummer: string })[];
  tickets: { id: string; nummer: string; typ: TicketTyp; titel: string; status: string; faellig_am: string; abgeschlossen_am: string | null; zustaendig: string }[];
  verlauf: { zeitpunkt: string; benutzer: string | null; text: string }[];
}

const ART: Record<string, string> = { telefon: 'Telefon', mobil: 'Mobil', geschaeftlich: 'Geschäftlich' };

function VorgangTabelle({ titel, pfad, liste }: { titel: string; pfad: string; liste: VorgangKurz[] }) {
  const navigate = useNavigate();
  return (
    <Card title={`${titel} (${liste.length})`} padded={false}>
      {liste.length === 0 ? (
        <Empty>Keine.</Empty>
      ) : (
        <table>
          <tbody>
            {liste.map((v) => {
              const offen = v.status !== 'abgeschlossen' && v.status !== 'storniert';
              return (
                <tr key={v.id} className="klickbar" onClick={() => navigate(`${pfad}/${v.id}`)}>
                  <td>
                    {v.titel}
                    <div className="small muted mono">{v.nummer}</div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {offen ? (
                      <Badge kind={v.faellig_am && new Date(v.faellig_am) < new Date() ? 'bad' : 'info'}>{v.status_bezeichnung}</Badge>
                    ) : (
                      <Badge kind={v.status === 'abgeschlossen' ? 'ok' : 'default'}>{v.status}</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export function KundeAktePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const ladenRolle = can('admin', 'verkauf', 'techniker');
  const [akte, setAkte] = useState<Akte | null>(null);
  const [fehler, setFehler] = useState('');
  const [bearbeiten, setBearbeiten] = useState(false);
  const [ticket, setTicket] = useState(false);
  const [zusammen, setZusammen] = useState(false);
  const [ziel, setZiel] = useState<Kunde | null>(null);

  const laden = useCallback(() => {
    api<Akte>(`/api/kunden/${id}`)
      .then(setAkte)
      .catch((e) => setFehler(errorText(e)));
  }, [id]);
  useEffect(laden, [laden]);

  if (!akte) return fehler ? <Notice kind="error">{fehler}</Notice> : <Loading />;
  const k = akte.kunde;

  async function zusammenfuehren() {
    if (!ziel) return;
    try {
      await api(`/api/kunden/${k.id}/zusammenfuehren`, { method: 'POST', body: { zielId: ziel.id } });
      navigate(`/kunden/${ziel.id}`);
      setZusammen(false);
    } catch (e) {
      setFehler(errorText(e));
    }
  }

  return (
    <>
      <div className="kopf">
        <div>
          <div className="muted small">
            <Link to="/kunden">Kunden</Link> / <span className="mono">{k.kundennummer}</span>
          </div>
          <h1>{kundenName(k)}</h1>
        </div>
        <span className="spacer" />
        {!k.zusammengefuehrt_in && (
          <>
            <button onClick={() => setBearbeiten(true)}>Bearbeiten</button>
            {ladenRolle && (
              <>
                <Link className="button" to={`/bestellungen/neu?kunde=${k.id}`}>
                  + Bestellung
                </Link>
                <Link className="button" to={`/service/neu?kunde=${k.id}`}>
                  + Gerät annehmen
                </Link>
                {can('admin', 'verkauf') && (
                  <Link className="button" to={`/lieferungen/neu?kunde=${k.id}`}>
                    + Lieferung
                  </Link>
                )}
              </>
            )}
            <button className="primary" onClick={() => setTicket(true)}>
              + Ticket
            </button>
          </>
        )}
      </div>

      {fehler && <Notice kind="error">{fehler}</Notice>}
      {k.zusammengefuehrt_in && (
        <Notice kind="warn">
          Dieser Datensatz war eine Dublette und wurde in <Link to={`/kunden/${k.zusammengefuehrt_in}`}>{k.zusammengefuehrt_in_nummer}</Link> überführt.
        </Notice>
      )}

      <div className="spalten haupt-rechts">
        <div className="stack">
          <Card title="Kontakt">
            <div className="stack" style={{ gap: 10 }}>
              <div>
                {k.strasse && <div>{k.strasse}</div>}
                <div>{[k.plz, k.ort].filter(Boolean).join(' ')}</div>
                {!k.strasse && !k.ort && <span className="muted">Keine Adresse erfasst</span>}
              </div>
              {k.telefone.map((t) => (
                <div key={t.id}>
                  <span className="small muted">{ART[t.art ?? 'telefon']}: </span>
                  <a href={`tel:${t.nummer.replace(/[^\d+]/g, '')}`}>{t.nummer}</a>
                </div>
              ))}
              {k.email && (
                <div>
                  <a href={`mailto:${k.email}`}>{k.email}</a>
                </div>
              )}
              {k.notiz && <div style={{ whiteSpace: 'pre-wrap' }} className="small">{k.notiz}</div>}
              <div className="small muted">
                Angelegt {formatDateTime(k.angelegt_am)}
                {k.angelegt_von_name && ` von ${k.angelegt_von_name}`}
              </div>
              {can('admin') && !k.zusammengefuehrt_in && (
                <div>
                  <button className="link small" onClick={() => setZusammen(true)}>
                    Ist eine Dublette – in anderen Kunden überführen …
                  </button>
                </div>
              )}
            </div>
          </Card>
          {ladenRolle && (
            <>
              <VorgangTabelle titel="Bestellungen" pfad="/bestellungen" liste={akte.bestellungen} />
              <VorgangTabelle titel="Service" pfad="/service" liste={akte.service} />
              {can('admin', 'verkauf') && (
                <>
                  <VorgangTabelle titel="Lieferungen" pfad="/lieferungen" liste={akte.lieferungen} />
                  <VorgangTabelle
                    titel="Forderungen"
                    pfad="/forderungen"
                    liste={akte.forderungen.map((f) => ({
                      ...f,
                      titel: `${formatGeld(f.betrag_cent)}${f.bezahlt_cent < f.betrag_cent ? ` · offen ${formatGeld(f.betrag_cent - f.bezahlt_cent)}` : ''}`,
                    }))}
                  />
                </>
              )}
            </>
          )}
        </div>

        <div className="stack">
          <Card title={`Tickets (${akte.tickets.length})`} padded={false}>
            {akte.tickets.length === 0 ? (
              <Empty>Keine Tickets.</Empty>
            ) : (
              <table>
                <tbody>
                  {akte.tickets.map((t) => (
                    <tr key={t.id} className="klickbar" onClick={() => navigate(`/tickets/${t.id}`)}>
                      <td style={{ width: 30 }}>{TYP_SYMBOL[t.typ]}</td>
                      <td>
                        {t.titel}
                        <div className="small muted">
                          <span className="mono">{t.nummer}</span> · {t.zustaendig}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {t.status === 'offen' ? (
                          <Badge kind={new Date(t.faellig_am) < new Date() ? 'bad' : 'info'}>{formatFrist(t.faellig_am)}</Badge>
                        ) : (
                          <Badge kind={t.status === 'erledigt' ? 'ok' : 'default'}>{t.status}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
          <Card title="Verlauf">
            <div className="verlauf">
              {akte.verlauf.map((v, i) => (
                <div className="eintrag" key={i}>
                  <div className="wann">
                    {formatDateTime(v.zeitpunkt)}
                    <br />
                    {v.benutzer ?? 'System'}
                  </div>
                  <div>{v.text}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {bearbeiten && (
        <Dialog titel="Kunde bearbeiten" breit onClose={() => setBearbeiten(false)}>
          <KundeFormular
            kunde={k}
            onGespeichert={() => {
              setBearbeiten(false);
              laden();
            }}
            onAbbrechen={() => setBearbeiten(false)}
          />
        </Dialog>
      )}
      {ticket && (
        <TicketNeuDialog
          kunde={k}
          onClose={() => setTicket(false)}
          onAngelegt={() => {
            setTicket(false);
            laden();
          }}
        />
      )}
      {zusammen && (
        <Dialog
          titel="Dublette überführen"
          onClose={() => setZusammen(false)}
          aktionen={
            <>
              <button onClick={() => setZusammen(false)}>Abbrechen</button>
              <button className="primary" disabled={!ziel || ziel.id === k.id} onClick={() => void zusammenfuehren()}>
                Überführen
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            <strong>{kundenName(k)}</strong> ({k.kundennummer}) wird in den gewählten Kunden überführt. Telefonnummern und Tickets wandern mit, leere
            Felder werden ergänzt. Dieser Datensatz bleibt als Verweis bestehen.
          </p>
          <KundenAuswahl gewaehlt={ziel} onWahl={setZiel} />
        </Dialog>
      )}
    </>
  );
}
